import "core-js/modules/es.array.sort.js";
var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
/**
 * Creates a network cache with configurable options
 */
export function createNetworkCache() {
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    maxSizeBytes = typeof window !== "undefined" ? 50 * 1024 * 1024 // 50MB for browsers
    : 500 * 1024 * 1024,
    // 500MB for Node.js
    logger = () => {} // no-op logger
  } = options;
  const cacheEntries = new Map();
  const cacheInfo = {
    maxSizeBytes,
    currentSizeBytes: 0
  };
  const deleteCacheEntry = key => {
    const entry = cacheEntries.get(key);
    if (entry) {
      logger("[networkCache] deleting ".concat(key));
      cacheInfo.currentSizeBytes -= entry.sizeBytes || 0;
      cacheEntries.delete(key);
    }
  };
  // Get or create a cached value
  const getOrCreate = (key, alwaysKeepInCache, factory) => __awaiter(this, void 0, void 0, function* () {
    // Check if we have a non-expired entry
    const existing = cacheEntries.get(key);
    const now = Date.now();
    if (existing) {
      logger("[networkCache] hit ".concat(key));
      // Update access stats
      existing.accessCount += 1;
      existing.lastAccessed = now;
      existing.alwaysKeep = alwaysKeepInCache;
      return existing.value;
    }
    logger("[networkCache] miss ".concat(key));
    // Execute the factory promise
    const promise = factory();
    // Store in cache
    cacheEntries.set(key, {
      timestamp: now,
      accessCount: 1,
      lastAccessed: now,
      value: promise,
      alwaysKeep: alwaysKeepInCache
    });
    try {
      const result = yield promise;
      return result;
    } catch (error) {
      // Handle rejection synchronously
      deleteCacheEntry(key);
      throw error;
    }
  });
  const pruneCache = () => {
    if (cacheInfo.currentSizeBytes <= cacheInfo.maxSizeBytes) {
      return;
    }
    logger("[networkCache] pruning cache. CurrentSize: ".concat(cacheInfo.currentSizeBytes, " bytes"));
    // Sort entries by last access time
    const entries = Array.from(cacheEntries.entries());
    entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
    const now = Date.now();
    const TEN_MINUTES = 10 * 60 * 1000; // 10 minutes in milliseconds
    for (const [key, entry] of entries) {
      if (entry.alwaysKeep || entry.sizeBytes === undefined) {
        continue;
      }
      // Delete if entry is old OR we still need space
      if (now - entry.lastAccessed > TEN_MINUTES || cacheInfo.currentSizeBytes > cacheInfo.maxSizeBytes) {
        deleteCacheEntry(key);
      } else {
        break;
      }
    }
    logger("[networkCache] post-prune cacheSize ".concat(cacheInfo.currentSizeBytes, " bytes"));
  };
  return {
    getOrCreate,
    pruneCache,
    setSizeOfEntry: (key, size) => {
      const entry = cacheEntries.get(key);
      if (entry) {
        if (size > maxSizeBytes) {
          console.warn("[networkCache] size of entry ".concat(key, " is greater than maxSizeBytes: ").concat(size, " bytes. Key: ").concat(key));
          logger("[networkCache] size of entry ".concat(key, " is greater than maxSizeBytes: ").concat(size, " bytes. Key: ").concat(key));
          deleteCacheEntry(key);
          return;
        }
        const sanitizedSize = Math.max(size, 1024); // minimum size of 1KB
        entry.sizeBytes = sanitizedSize;
        cacheInfo.currentSizeBytes += sanitizedSize;
      }
      if (size > cacheInfo.maxSizeBytes / 5) {
        console.warn("Large file detected in cache: ".concat(size, " bytes. Key: ").concat(key));
        logger("[networkCache] Large file detected in cache: ".concat(size, " bytes. Key: ").concat(key));
      }
      logger("[networkCache] cacheSize ".concat(cacheInfo.currentSizeBytes, " bytes"));
      pruneCache();
    },
    getCacheSize: () => cacheInfo.currentSizeBytes,
    cache: cacheEntries
  };
}
// Create a default shared cache instance
export let defaultNetworkCache = createNetworkCache();
export const getNetworkCache = () => defaultNetworkCache;
export const setNetworkCache = cache => {
  defaultNetworkCache = cache;
};